/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * types.ts - Types for the question screen
 */

import { ImageRequire } from '../../types';
import { reverseMappingForStringEnums, unreachable } from '../../utils/typescript';

export type Formation = (0 | 1)[][];

export enum Format {
  /** A symbol B = ? */
  Equation1 = 1,
  /** ? = A symbol B */
  Equation2,
  /** B symbol A = ? */
  Equation3,
  /** ? = B symbol A */
  Equation4,
  /** A symbol ? = B */
  Equation5,
  /** ? symbol A = B */
  Equation6,
  /** B = A symbol ? */
  Equation7,
  /** B = ? symbol A */
  Equation8,
  /** action A is ? */
  WordEquation,
  /** Sequence with step size step, ? in 1st position */
  AdditiveSequence1,
  /** Sequence with step size step, ? in 2nd position */
  AdditiveSequence2,
  /** Sequence with step size step, ? in 3rd position */
  AdditiveSequence3,
  /** Sequence with step size step, ? in 4th position */
  AdditiveSequence4,
  /** Sequence with step size step, ? in 5th position */
  AdditiveSequence5,
  /** Odd or even? */
  OddOrEven,
  /** Equal groups AB */
  EqualGroupsAB,
  /** Equal groups AAB */
  EqualGroupsAAB,
  /** Equal groups ABA */
  EqualGroupsABA,
  /** Equal groups BAA */
  EqualGroupsBAA,
  /** Equal groups BAAA */
  EqualGroupsBAAA,
  /** Equal groups ABAA */
  EqualGroupsABAA,
  /** Equal groups AABA */
  EqualGroupsAABA,
  /** Equal groups AAAB */
  EqualGroupsAAAB,
  /** Equal groups ABBA */
  EqualGroupsABBA,
  /** Equal groups ABAB */
  EqualGroupsABAB,
  /** Equal groups counters AB */
  EqualGroupsCountersAB,
  /** Equal groups counters ABC */
  EqualGroupsCountersABC,
  /** Equal groups counters ABCD */
  EqualGroupsCountersABCD,
}

// Convenience subgroups of formats
const FormatGroupEquationArray = [
  Format.Equation1,
  Format.Equation2,
  Format.Equation3,
  Format.Equation4,
  Format.Equation5,
  Format.Equation6,
  Format.Equation7,
  Format.Equation8,
] as const;
export type FormatGroupEquation = typeof FormatGroupEquationArray[number];

const FormatGroupAdditiveSequenceArray = [
  Format.AdditiveSequence1,
  Format.AdditiveSequence2,
  Format.AdditiveSequence3,
  Format.AdditiveSequence4,
  Format.AdditiveSequence5,
] as const;
export type FormatGroupAdditiveSequence = typeof FormatGroupAdditiveSequenceArray[number];

const FormatGroupEqualGroupsArray = [
  Format.EqualGroupsAB,
  Format.EqualGroupsAAB,
  Format.EqualGroupsABA,
  Format.EqualGroupsBAA,
  Format.EqualGroupsBAAA,
  Format.EqualGroupsABAA,
  Format.EqualGroupsAABA,
  Format.EqualGroupsAAAB,
  Format.EqualGroupsABBA,
  Format.EqualGroupsABAB,
] as const;
export type FormatGroupEqualGroups = typeof FormatGroupEqualGroupsArray[number];

const FormatGroupEqualGroupsCountersArray = [
  Format.EqualGroupsCountersAB,
  Format.EqualGroupsCountersABC,
  Format.EqualGroupsCountersABCD,
] as const;
export type FormatGroupEqualGroupsCounters = typeof FormatGroupEqualGroupsCountersArray[number];

export enum Hint {
  SEQUENCE = 'Sequence',
  ADDITION_TEN_FRAME = 'Ten Frame',
  ODD_EVEN_TEN_FRAME = 'Odd Even Ten Frame',
  MULTIPLY_TWO_TEN_FRAME = 'Multiply 2 Ten Frame',
  FIVE_DICE_TEN_FRAME = 'Five Dice Ten Frame',
  DOUBLE_TEN_FRAMES = 'Double Ten Frames',
  MULTIPLE_TEN_FRAMES = 'Multiple Ten Frames',
  TEN_FRAME_SUBTRACT = 'TenFrameSubtract',
  BASE_TEN = 'Base Ten',
  BASE_TEN_MULTIPLICATION = 'Base Ten Multiplication',
  DIVIDE_BASE_TEN = 'Divide Base Ten',
  TWO_BASE_TENS = 'Two Base Tens',
  TWO_BASE_TENS_SUBTRACTION = 'Two Base Tens Subtraction',
  DIVIDE_ARRAY = 'Divide Array',
  MULTIPLICATION_ARRAY = 'Multiplication Array',
  BAR_MODEL = 'Bar Model',
  HUNDRED_SQUARE = '100 Square',
}
export const HintKeys = reverseMappingForStringEnums(Hint);
 
interface QuestionBase {
  readonly format: Format
  readonly hint: Hint
}

// Question types
export interface EquationQuestion extends QuestionBase {
  readonly format: FormatGroupEquation
  readonly hint: Hint.ADDITION_TEN_FRAME | Hint.TWO_BASE_TENS | Hint.HUNDRED_SQUARE | Hint.TEN_FRAME_SUBTRACT | Hint.TWO_BASE_TENS_SUBTRACTION | Hint.MULTIPLY_TWO_TEN_FRAME | Hint.FIVE_DICE_TEN_FRAME | Hint.BASE_TEN | Hint.BASE_TEN_MULTIPLICATION | Hint.DIVIDE_ARRAY | Hint.MULTIPLICATION_ARRAY | Hint.DIVIDE_BASE_TEN | Hint.BAR_MODEL
  readonly numA: number
  readonly numB: number
  readonly sign: string
  readonly answer: number
}
export interface WordEquationQuestion extends QuestionBase {
  readonly format: Format.WordEquation
  readonly hint: Hint.DOUBLE_TEN_FRAMES | Hint.BAR_MODEL
  readonly numA: number
  readonly action: string
  readonly answer: number
}
export interface AdditiveSequenceQuestion extends QuestionBase {
  readonly numA: number // TODO: this is always equal to answer - remove it?
  readonly format: FormatGroupAdditiveSequence
  readonly hint: Hint.SEQUENCE
  readonly answer: number
  readonly sequence: number
}
export interface EqualGroupsQuestion extends QuestionBase {
  readonly format: FormatGroupEqualGroups
  readonly hint: Hint.MULTIPLE_TEN_FRAMES
  readonly numA: number
  readonly numB: number
  readonly answer: 'Yes' | 'No'
  readonly action: string
  readonly imageA: ImageRequire
  readonly imageB: ImageRequire
}
export interface EqualGroupsCountersQuestionBase extends QuestionBase {
  readonly format: FormatGroupEqualGroupsCounters
  readonly hint: Hint.MULTIPLE_TEN_FRAMES
  readonly numA: number
  readonly numB: number
  readonly answer: 'Yes' | 'No'
  readonly action: string
}
// Equal group counters questions have varying contents depending on the format.
export interface EqualGroupsCountersABQuestion extends EqualGroupsCountersQuestionBase {
  readonly format: Format.EqualGroupsCountersAB 
}
export interface EqualGroupsCountersABCQuestion extends EqualGroupsCountersQuestionBase {
  readonly format: Format.EqualGroupsCountersABC
  readonly numC: number
}
export interface EqualGroupsCountersABCDQuestion extends EqualGroupsCountersQuestionBase {
  readonly format: Format.EqualGroupsCountersABCD
  readonly numC: number
  readonly numD: number
}
export interface OddOrEvenQuestion extends QuestionBase {
  readonly format: Format.OddOrEven
  readonly hint: Hint.ODD_EVEN_TEN_FRAME
  readonly numA: number
  readonly action: string
  readonly answer: 'Odd' | 'Even'
}

// Type guards
export function isEquationQuestion(question: Question): question is EquationQuestion {
  return FormatGroupEquationArray.some(format => format === question.format);
}
export function isWordEquationQuestion(question: Question): question is WordEquationQuestion {
  return Format.WordEquation === question.format;
}
export function isAdditiveSequenceQuestion(question: Question): question is AdditiveSequenceQuestion {
  return FormatGroupAdditiveSequenceArray.some(format => format === question.format);
}
export function isEqualGroupsQuestion(question: Question): question is EqualGroupsQuestion {
  return FormatGroupEqualGroupsArray.some(format => format === question.format);
}
export function isEqualGroupsCountersQuestion(question: Question): question is EqualGroupsCountersQuestion {
  return FormatGroupEqualGroupsCountersArray.some(format => format === question.format);
}
export function isOddOrEvenQuestion(question: Question): question is OddOrEvenQuestion {
  return Format.OddOrEven === question.format;
}

// Equals checking
export function equalsQuestion(a: Question, b: Question): boolean {
  // Check common fields:
  const commonAgree = a.format === b.format &&
    a.hint === b.hint;
  if (!commonAgree) { return false; }

  // Check specific fields.
  if (isEquationQuestion(a)) {
    return isEquationQuestion(b) &&
      a.numA === b.numA &&
      a.numB === b.numB &&
      a.sign === b.sign &&
      a.answer === b.answer;
  } else if (isWordEquationQuestion(a)) {
    return isWordEquationQuestion(b) &&
      a.numA === b.numA &&
      a.action === b.action &&
      a.answer === b.answer;
  } else if (isAdditiveSequenceQuestion(a)) {
    return isAdditiveSequenceQuestion(b) &&
      a.numA === b.numA &&
      a.answer === b.answer &&
      a.sequence === b.sequence;
  } else if (isEqualGroupsQuestion(a)) {
    return isEqualGroupsQuestion(b) &&
      a.numA === b.numA &&
      a.numB === b.numB &&
      a.answer === b.answer &&
      a.action === b.action &&
      a.imageA === b.imageA &&
      a.imageB === b.imageB;
  } else if (isEqualGroupsCountersQuestion(a)) {
    return isEqualGroupsCountersQuestion(b) &&
      a.numA === b.numA &&
      a.numB === b.numB &&
      a.answer === b.answer &&
      a.action === b.action;
  } else if (isOddOrEvenQuestion(a)) {
    return isOddOrEvenQuestion(b) &&
      a.numA === b.numA &&
      a.answer === b.answer &&
      a.action === b.action;
  } else {
    unreachable(a);
  }
}

export type EqualGroupsCountersQuestion = 
  | EqualGroupsCountersABQuestion
  | EqualGroupsCountersABCQuestion
  | EqualGroupsCountersABCDQuestion;

/** Raw question data. */
export type Question = 
  | EquationQuestion
  | WordEquationQuestion
  | AdditiveSequenceQuestion
  | EqualGroupsQuestion
  | EqualGroupsCountersQuestion
  | OddOrEvenQuestion;

// Some question types come with extra parameters, which are added in question generation
export type EqualGroupsCountersQuestionWithExtraParams =
  | EqualGroupsCountersABQuestion & {
    formationA: Formation, 
    formationB: Formation, 
  }
  | EqualGroupsCountersABCQuestion & {
    formationA: Formation, 
    formationB: Formation, 
    formationC: Formation,
  }
  | EqualGroupsCountersABCDQuestion & {
    formationA: Formation, 
    formationB: Formation,
    formationC: Formation,
    formationD: Formation 
  };

export type QuestionWithExtraParameters =
  | EquationQuestion
  | WordEquationQuestion
  | AdditiveSequenceQuestion
  | EqualGroupsQuestion
  | EqualGroupsCountersQuestionWithExtraParams
  | OddOrEvenQuestion;

export interface Result {
  question: string,
  answer: Question['answer'],
  userAnswer?: Question['answer'],
  correct: boolean,
}
